import type { ThreadMessage } from '@assistant-ui/react';
import { errorForStatus } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { handleSessionExpired } from '@/shared/auth/logout';

export type NativeEvent = {
  schema_version: number;
  event_id: string;
  run_id: string;
  thread_id: string;
  sequence: number;
  source: string;
  event_type: string;
  timestamp: string;
  parent_event_id?: string | null;
  payload: Record<string, unknown>;
};

export type NativeStreamInput = {
  threadId: string;
  messages: unknown[];
  resume?: unknown[];
  runConfig?: unknown;
  afterSequence?: number;
};

export type NativeStreamRunResult = 'completed' | 'interrupted';

export type NativeStreamAccepted = {
  threadId: string;
  runId: string | null;
};

type NativeContentPart = {
  type: string;
  text?: string;
  image?: string;
  data?: string;
  mimeType?: string;
  filename?: string;
  [key: string]: unknown;
};

function messageContent(message: ThreadMessage): unknown {
  const parts: NativeContentPart[] = (message.content as NativeContentPart[]).flatMap((part): NativeContentPart[] => {
    if (part.type === 'text') return [{ type: 'text', text: part.text }];
    if (part.type === 'image') {
      return [{ type: 'image', source: { type: 'url', value: part.image }, metadata: { filename: part.filename } }];
    }
    if (part.type === 'file') {
      return [{ type: 'document', source: { type: 'url', value: part.data, mimeType: part.mimeType }, metadata: { filename: part.filename } }];
    }
    return [];
  });
  const first = parts[0] as { type?: string; text?: string } | undefined;
  return parts.length === 1 && first?.type === 'text' ? first.text : parts;
}

type AttachmentLike = { content?: unknown };

/** Flattens assistant-ui `Attachment[]` (file/image parts) into the native wire's
 * `document`/`image` content-part shape `native_intake.py::_content_part_artifact_id` reads. */
export function attachmentContentParts(attachments: readonly AttachmentLike[] | undefined): NativeContentPart[] {
  return (attachments ?? []).flatMap((attachment) =>
    (attachment.content as Array<{ type: string; data?: string; image?: string; mimeType?: string; filename?: string }>).flatMap((part) => {
      if (part.type !== 'file' && part.type !== 'image') return [];
      return [{
        type: part.type === 'image' ? 'image' : 'document',
        source: { type: 'url', value: part.image ?? part.data, ...(part.mimeType ? { mimeType: part.mimeType } : {}) },
        ...(part.filename ? { metadata: { filename: part.filename } } : {}),
      }];
    }),
  );
}

export function nativeMessages(messages: readonly ThreadMessage[]): unknown[] {
  return messages.map((message) => {
    const attachments = attachmentContentParts(message.attachments);
    const baseContent = messageContent(message);
    const content = attachments.length === 0
      ? baseContent
      : [...(typeof baseContent === 'string' ? [{ type: 'text', text: baseContent }] : baseContent as unknown[]), ...attachments];
    return { id: message.id, role: message.role, content };
  });
}

export class NativeStreamClient {
  threadId: string;
  runId: string | null = null;
  private controller: AbortController | null = null;

  constructor(private readonly url: string, threadId: string | null, _timeoutMs: number) {
    this.threadId = threadId ?? crypto.randomUUID();
  }

  abortRun(): void {
    this.controller?.abort();
  }

  async run(
    input: Omit<NativeStreamInput, 'threadId'>,
    onEvent: (event: NativeEvent) => void,
    onAccepted?: (accepted: NativeStreamAccepted) => void,
  ): Promise<NativeStreamRunResult> {
    this.controller = new AbortController();
    // The server emits heartbeat frames while a run is active. A total client
    // timeout incorrectly aborts legitimate long-running work; cancellation
    // remains explicit through abortRun().
    const signal = this.controller.signal;
    const token = getToken();
    const url = input.afterSequence && input.afterSequence > 0
      ? `${this.url}${this.url.includes('?') ? '&' : '?'}after_sequence=${encodeURIComponent(input.afterSequence)}`
      : this.url;
    const response = await fetch(url, {
      method: 'POST',
      signal,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        threadId: this.threadId,
        messages: input.messages,
        ...(input.resume ? { resume: input.resume } : {}),
        ...(input.runConfig ? { forwardedProps: { runConfig: input.runConfig } } : {}),
      }),
    });
    if (!response.ok) {
      if (response.status === 401) handleSessionExpired();
      throw errorForStatus(response.status, await response.text(), { status: response.status, url, method: 'POST' });
    }
    const returnedThreadId = response.headers.get('X-Conversation-ID');
    if (returnedThreadId) this.threadId = returnedThreadId;
    const returnedRunId = response.headers.get('X-Run-ID');
    if (returnedRunId) this.runId = returnedRunId;
    if (returnedThreadId) onAccepted?.({ threadId: returnedThreadId, runId: this.runId });
    if (!response.body) throw new Error('Agent stream did not include a response body');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let sawTerminalEvent = false;
    let sawInterruptEvent = false;
    for (;;) {
      const chunk = await reader.read();
      buffer += decoder.decode(chunk.value ?? new Uint8Array(), { stream: !chunk.done });
      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const data = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim())
          .join('');
        if (data) {
          const event = JSON.parse(data) as NativeEvent;
          onEvent(event);
          if (event.event_type === 'done' || event.event_type === 'error') sawTerminalEvent = true;
          if (event.event_type === 'user_question' || event.event_type === 'approval_requested') {
            sawInterruptEvent = true;
          }
        }
        boundary = buffer.indexOf('\n\n');
      }
      if (chunk.done) break;
    }
    if (sawInterruptEvent) return 'interrupted';
    if (!sawTerminalEvent) throw new Error('Agent stream ended before a terminal event');
    return 'completed';
  }
}
